import IconAvatar from "@/components/icon-avatar";
import { useSession, useUser } from "@clerk/react";
import { Button, Card, Chip, Skeleton, useOverlayState, toast } from "@heroui/react";
import {
  CalendarDays,
  CalendarX,
  Camera,
  Circle,
  Dot,
  Hand,
  Mic,
  MoveRight,
  Play,
  RefreshCw,
  Users,
} from "lucide-react";
import {
  getMyMeetsUpComing as getMyMeetsUpComingApi,
  getMyMeetsToday as getMyMeetsTodayApi,
  getMyMeetsByDateRange as getMyMeetsByDateRangeApi,
} from "@/api/meets";
import { getMyConnections as getMyConnectionsApi } from "@/api/users";
import { getUserRecordings as getUserRecordingsApi } from "@/api/audio";
import {
  getCountdownLabel,
  getRelativeDateTimeLabel,
  getWeekDays,
  getPastDateRange,
  getReadableTimeDiff,
  isMeetingActive,
  getCurrentWeekDateRange,
} from "@/utils/dates";
import { cn } from "@/utils/cn";
import { useEffect, useState } from "react";
import { groupMeetsByDate, normalizeMeets } from "@/utils/meets";
import MemberAvatar from "@/components/members/member-avatar";
import LoopDetailsDrawer from "@/components/desktop/drawers/loop-details-drawer";
import RecordingDetailsDrawer from "@/components/desktop/drawers/recording-details-drawer";
import NetworkDetailsDrawer from "@/components/desktop/drawers/network-details-drawer";
import { useNavigate } from "react-router-dom";
import ExtraCountAvatar from "@/components/members/extra-count-avatar";
import CountdownBadge from "@/components/desktop/countdown-badge";

const ClickableMemberAvatar = ({ member, size, onOpenConnection }) => {
  if (!member.id) return <MemberAvatar member={member} size={size} />;

  return (
    <button
      type="button"
      title={member.fullname}
      onClick={(e) => {
        e.stopPropagation();
        onOpenConnection(member.id);
      }}
      className="rounded-full transition-transform hover:scale-105 hover:z-10"
    >
      <MemberAvatar member={member} size={size} />
    </button>
  );
};

const KPICard = ({ icon, label, sub, className, value, isLoading, onClick }) => {
  const Icon = icon;
  return (
    <Card
      onClick={onClick}
      className="border border-accent/0 hover:border-accent hover:bg-accent-soft cursor-pointer transition-all duration-300"
    >
      <Card.Content className="flex-row items-center lg:gap-4 gap-2">
        <IconAvatar
          icon={Icon}
          color={"accent"}
          className={"rounded-full lg:size-14 text-[20px]"}
          iconClassName={"lg:size-8"}
          strokeWidth={1.1}
        />
        <div className="flex flex-col gap-2">
          <span className="lg:text-xl text-base font-bold text-accent">
            {isLoading ? <span className="inline-block h-5 w-6 rounded bg-default/40 animate-pulse" /> : value}
          </span>
          <span className="font-semibold lg:text-base text-sm">{label}</span>
          <span className="text-muted text-xs">{sub}</span>
        </div>
      </Card.Content>
    </Card>
  );
};

const NoMeetsToday = () => (
  <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
    <div className="rounded-full bg-default/60 p-4">
      <CalendarX className="size-6 text-muted" />
    </div>
    <div>
      <p className="font-semibold">No meetings scheduled</p>
      <p className="text-sm text-muted">Nothing planned for this day — enjoy the breathing room.</p>
    </div>
  </div>
);

const EmptyRow = ({ label }) => <p className="text-sm text-muted py-4 text-center">{label}</p>;

const toLoopRecord = (meet) => ({
  _id: meet.id,
  title: meet.title,
  description: meet.subtitle,
  meeting_date: meet.meets_date,
  start_time: { set: meet.start_time, real: "" },
  end_time: { set: meet.end_time, real: "" },
  short_code: meet.shortCode,
  members: (meet.members ?? []).map((m) => ({
    member_id: m.id,
    member_email: m.email,
    member_fullname: m.fullname,
  })),
});

const DashboardPage = () => {
  const { user } = useUser();
  const { session } = useSession();

  const [activeMeets, setActiveMeets] = useState([]);
  const [upcomingMeets, setUpcomingMeets] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [rawRecordings, setRawRecordings] = useState([]);
  const [weekMeets, setWeekMeets] = useState([]);
  const [connectionsCount, setConnectionsCount] = useState(0);

  const [isLoadingToday, setIsLoadingToday] = useState(false);
  const [isLoadingUpcoming, setIsLoadingUpcoming] = useState(false);
  const [isLoadingRecordings, setIsLoadingRecordings] = useState(false);
  const [isLoadingWeek, setIsLoadingWeek] = useState(false);
  const [isLoadingConnections, setIsLoadingConnections] = useState(false);

  const navigate = useNavigate();

  const weekDays = getWeekDays();

  const [selectedDay, setSelectedDay] = useState(() => {
    const today = weekDays.find((d) => d.isToday);
    return today?.isoDate ?? weekDays[0]?.isoDate ?? null;
  });

  const weekMeetsByDate = groupMeetsByDate(weekMeets);
  const selectedDayMeets = normalizeMeets(weekMeetsByDate[selectedDay] || []).sort((a, b) => {
    const dateA = `${a.meets_date ?? ""} ${a.start_time ?? ""}`;
    const dateB = `${b.meets_date ?? ""} ${b.start_time ?? ""}`;
    return dateA.localeCompare(dateB);
  });

  // Drawers
  const loopDrawerState = useOverlayState();
  const [selectedLoop, setSelectedLoop] = useState(null);

  const recordingDrawerState = useOverlayState();
  const [selectedRecording, setSelectedRecording] = useState(null);

  const connectionDrawerState = useOverlayState();
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);

  const openLoopDetails = (meet) => {
    setSelectedLoop(toLoopRecord(meet));
    loopDrawerState.open();
  };

  const openRecordingDetails = (recordingId) => {
    const raw = rawRecordings.find((r) => r._id === recordingId);
    if (!raw) return;
    setSelectedRecording(raw);
    recordingDrawerState.open();
  };

  const openConnectionDetails = (connectionId) => {
    setSelectedConnectionId(connectionId);
    connectionDrawerState.open();
  };

  useEffect(() => {
    if (!session) return;
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getData = async () => {
    await Promise.all([getTodaysMeets(), getUpcomingMeets(), getWeekMeets(), getRecordings(), getConnections()]);
  };

  const getTodaysMeets = async () => {
    setIsLoadingToday(true);
    const token = await session.getToken();

    return getMyMeetsTodayApi(token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          const active = (_resp.data.Records ?? []).filter((m) =>
            isMeetingActive(m["meeting_date"], m["start_time"]?.set, m["end_time"]?.set),
          );
          setActiveMeets(normalizeMeets(active));
        } else {
          throw new Error("Failed to load today's meets");
        }
      })
      .catch((err) => {
        console.log(err);
        toast.warning("Could not fetch active loops");
      })
      .finally(() => setIsLoadingToday(false));
  };

  const getUpcomingMeets = async () => {
    setIsLoadingUpcoming(true);
    const token = await session.getToken();

    return getMyMeetsUpComingApi(token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setUpcomingMeets(normalizeMeets(_resp.data.Records ?? []));
        } else {
          throw new Error("Failed to load upcoming meets");
        }
      })
      .catch((err) => {
        console.log(err);
        toast.warning("Could not fetch upcoming loops");
      })
      .finally(() => setIsLoadingUpcoming(false));
  };

  const getWeekMeets = async () => {
    setIsLoadingWeek(true);
    const token = await session.getToken();
    const dateRange = [weekDays[0].isoDate, weekDays[6].isoDate];

    return getMyMeetsByDateRangeApi(dateRange, token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setWeekMeets(_resp.data.Records ?? []);
        } else {
          throw new Error("Failed to load this week's meets");
        }
      })
      .catch((err) => {
        console.log(err);
        toast.warning("Could not fetch your week");
      })
      .finally(() => setIsLoadingWeek(false));
  };

  const getRecordings = async () => {
    setIsLoadingRecordings(true);
    const token = await session.getToken();

    return getUserRecordingsApi("current", getCurrentWeekDateRange(), token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          const records = _resp.data.Records ?? [];
          setRawRecordings(records);
          setRecordings(normalizeMeets(records));
        } else {
          throw new Error("Failed to load recordings");
        }
      })
      .catch((err) => {
        console.log(err);
        toast.warning("Could not fetch recordings");
      })
      .finally(() => setIsLoadingRecordings(false));
  };

  const getConnections = async () => {
    setIsLoadingConnections(true);
    const token = await session.getToken();

    return getMyConnectionsApi(token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setConnectionsCount((_resp.data.Records ?? []).length);
        } else {
          throw new Error("Failed to load connections");
        }
      })
      .catch((err) => {
        console.log(err);
        toast.warning("Could not fetch connections");
      })
      .finally(() => setIsLoadingConnections(false));
  };

  const recordingsThisWeek = recordings.filter(
    (r) => r.meets_date >= weekDays[0].isoDate && r.meets_date <= weekDays[6].isoDate,
  ).length;

  const recentRecordings = recordings.slice(0, 3);

  return (
    <div className="flex flex-col gap-4 desktop-page-padding">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-2xl flex items-center font-extrabold">
            Welcome back, <span>{user.firstName}!</span> <Hand fill="gold" />
          </span>

          <span className="text-sm text-muted">Here's what's happening in your loop today</span>
        </div>

        <div>
          <Button
            variant="outline"
            size=""
            isIconOnly
            className={"bg-surface text-foreground rounded-xl font-normal"}
            onPress={getData}
          >
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Main */}
        <div className="lg:col-span-8 md:col-span-12 flex flex-col gap-6">
          <div className="grid grid-cols-2 desktop:grid-cols-4 gap-3">
            <KPICard
              icon={CalendarDays}
              label={"Upcoming"}
              value={upcomingMeets.length}
              sub={"Loops"}
              isLoading={isLoadingUpcoming}
              onClick={() => navigate("/loops")}
            />
            <KPICard
              icon={Users}
              label={"Connections"}
              value={connectionsCount}
              sub={"People in your loop"}
              isLoading={isLoadingConnections}
              onClick={() => navigate("/network")}
            />
            <KPICard
              icon={Mic}
              label={"Recordings"}
              value={recordingsThisWeek}
              sub={"This week"}
              isLoading={isLoadingRecordings}
              onClick={() => navigate("/recordings")}
            />
            <KPICard
              icon={Circle}
              label={"Active Loops"}
              value={activeMeets.length}
              sub={"Ongoing"}
              isLoading={isLoadingToday}
              onClick={() => navigate("/loops")}
            />
          </div>

          <Card>
            <Card.Header className="flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarDays />
                <span className="font-bold">Your Week</span>
              </div>
              <Button variant="outline" className={"text-accent"} size="sm" onPress={() => navigate('/calendar')}>
                <CalendarDays /> View Calendar
              </Button>
            </Card.Header>

            <Card.Content className="gap-6">
              <div className="grid grid-cols-7">
                {weekDays.map((day) => (
                  <div
                    key={day.isoDate}
                    onClick={() => setSelectedDay(day.isoDate)}
                    className={cn(
                      "flex flex-col items-center text-center cursor-pointer rounded-lg px-3 py-2 transition-colors duration-300",
                      selectedDay === day.isoDate
                        ? "bg-accent text-accent-foreground"
                        : day.isToday
                          ? "bg-accent/10 text-accent"
                          : "hover:bg-default",
                    )}
                  >
                    <span
                      className={cn("text-xs", selectedDay === day.isoDate ? "text-accent-foreground" : "text-muted")}
                    >
                      {day.dayLabel}
                    </span>
                    <span className="block text-base font-semibold">{day.dayNumber}</span>
                  </div>
                ))}
              </div>

              <div>
                {isLoadingWeek ? (
                  <div className="flex flex-col gap-3 py-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="h-16 w-full rounded-2xl bg-default/40 animate-pulse" />
                    ))}
                  </div>
                ) : selectedDayMeets.length === 0 ? (
                  <NoMeetsToday />
                ) : (
                  <div className="flex flex-col">
                    {selectedDayMeets.map((meet, index) => {
                      const isLast = index === selectedDayMeets.length - 1;
                      return (
                        <div key={meet.id} className="relative pl-6">
                          <div className="absolute left-0 top-0 bottom-0 w-3 flex flex-col items-center">
                            <div className="size-3 rounded-full bg-accent shrink-0 mt-1.5" />
                            {!isLast && <div className="w-[1px] flex-1 bg-accent" />}
                          </div>

                          <div className={cn("flex items-start gap-6", !isLast && "pb-4")}>
                            <div className="flex flex-col gap-2 w-16 shrink-0">
                              <span className="font-medium text-sm">{meet.start_time}</span>
                              <span className="text-xs text-muted">
                                {getReadableTimeDiff(meet.start_time, meet.end_time, "short")}
                              </span>
                            </div>

                            <Card
                              onClick={() => openLoopDetails(meet)}
                              className="shadow-none border grow hover:bg-accent-soft/50 hover:border-accent cursor-pointer transition-colors duration-300"
                            >
                              <Card.Content className="flex-row justify-between">
                                <div className="flex flex-col gap-2">
                                  <div className="flex items-center gap-1">
                                    <span className="font-bold">{meet.title}</span>
                                    <CountdownBadge loop={meet} />
                                  </div>
                                  <span className="text-sm text-muted">{meet.subtitle}</span>
                                </div>

                                <div className="flex items-center text-xs gap-1">
                                  {meet.visibleMembers.map((m) => (
                                    <ClickableMemberAvatar
                                      key={m.email}
                                      member={m}
                                      onOpenConnection={openConnectionDetails}
                                    />
                                  ))}
                                  {meet.extraCount > 0 && (
                                    <ExtraCountAvatar
                                      count={meet.extraCount}
                                      members={meet.members?.slice(meet.visibleMembers.length)}
                                    />
                                  )}
                                </div>
                              </Card.Content>
                            </Card>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card.Content>

            <Card.Footer>
              <Button variant="ghost" className={"text-accent"}>
                View all loops <MoveRight />
              </Button>
            </Card.Footer>
          </Card>

          <Card>
            <Card.Header className="flex-row justify-between items-center">
              <span className="font-bold">Active Loops</span>

              <Button variant="ghost">View All</Button>
            </Card.Header>

            <Card.Content className={cn(activeMeets.length > 0 && "grid grid-cols-3 gap-3")}>
              {isLoadingToday ? (
                <EmptyRow label="Loading active loops..." />
              ) : activeMeets.length === 0 ? (
                <EmptyRow label="No loops running right now." />
              ) : (
                activeMeets.map((meet) => (
                  <div
                    key={meet.id}
                    onClick={() => openLoopDetails(meet)}
                    className="flex gap-3 items-start border rounded-2xl p-3 cursor-pointer hover:bg-accent-soft/50 hover:border-accent transition-colors"
                  >
                    <IconAvatar icon={Camera} color={"accent"} className={"rounded-full"} />
                    <div className="grow flex flex-col gap-2">
                      <div className="flex flex-col">
                        <div className="flex items-center justify-between">
                          <span className="font-bold">{meet.title}</span>
                          <Dot className="fill-success text-success" />
                        </div>
                        <span className="text-xs text-muted">{meet.members.length} members</span>
                      </div>
                      <div className="flex items-center text-xs gap-1">
                        {meet.visibleMembers.map((m) => (
                          <ClickableMemberAvatar key={m.email} member={m} onOpenConnection={openConnectionDetails} />
                        ))}
                        {meet.extraCount > 0 && (
                          <ExtraCountAvatar
                            count={meet.extraCount}
                            members={meet.members?.slice(meet.visibleMembers.length)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </Card.Content>
          </Card>
        </div>

        {/* Side bar */}
        <div className="lg:col-span-4 col-span-12 w-full flex flex-col gap-6 sticky-side-bar">
          <Card>
            <Card.Header className="flex-row justify-between items-center">
              <span className="font-bold">Upcoming Loops</span>
              <Button variant="ghost" className="text-accent" onPress={() => navigate('/loops?filter=upcoming')}>
                View all
              </Button>
            </Card.Header>
            <Card.Content className="flex flex-col gap-3">
              {isLoadingUpcoming ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between border rounded-2xl p-4">
                    <div className="flex items-center gap-3">
                      <Skeleton className="size-10 rounded-full shrink-0" />
                      <div className="flex flex-col gap-2">
                        <Skeleton className="h-3.5 w-32 rounded" />
                        <Skeleton className="h-3 w-20 rounded" />
                        <Skeleton className="h-5 w-16 rounded-full" />
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Skeleton className="size-6 rounded-full" />
                      <Skeleton className="size-6 rounded-full" />
                    </div>
                  </div>
                ))
              ) : upcomingMeets.length === 0 ? (
                <EmptyRow label="Nothing coming up." />
              ) : (
                upcomingMeets.slice(0, 3).map((meet) => (
                  <div
                    key={meet.id}
                    onClick={() => openLoopDetails(meet)}
                    className="flex items-center justify-between border dark:border-muted/30 rounded-2xl p-4 cursor-pointer hover:bg-accent-soft/50 hover:border-accent transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <IconAvatar icon={CalendarDays} color={"accent"} className={"rounded-full"} strokeWidth={1.2} />
                      <div className="flex flex-col gap-1 items-start">
                        <span className="font-semibold text-sm">{meet.title}</span>
                        <span className="text-xs text-muted">
                          {getRelativeDateTimeLabel(meet.meets_date, meet.start_time)}
                        </span>
                        <Chip variant="soft" color="accent">
                          <Chip.Label>{getCountdownLabel(meet.meets_date, meet.start_time)}</Chip.Label>
                        </Chip>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {meet.visibleMembers.map((m) => (
                        <ClickableMemberAvatar
                          key={m.email}
                          member={m}
                          size="size-6"
                          onOpenConnection={openConnectionDetails}
                        />
                      ))}
                      {meet.extraCount > 0 && <ExtraCountAvatar count={meet.extraCount} size="size-6" />}
                    </div>
                  </div>
                ))
              )}
            </Card.Content>
          </Card>

          <Card>
            <Card.Header className="flex-row justify-between items-center">
              <span className="font-bold">Recent Recordings</span>
              <Button variant="ghost" className="text-accent" onPress={() => navigate('/recordings?filter=thisWeek')}>
                View all
              </Button>
            </Card.Header>
            <Card.Content className="flex flex-col gap-3">
              {isLoadingRecordings ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between border rounded-lg p-3">
                    <div className="flex gap-2 items-center">
                      <Skeleton className="size-10 rounded-full shrink-0" />
                      <div className="flex flex-col gap-2">
                        <Skeleton className="h-3.5 w-28 rounded" />
                        <Skeleton className="h-3 w-20 rounded" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-3 w-10 rounded" />
                      <Skeleton className="size-8 rounded-full" />
                    </div>
                  </div>
                ))
              ) : recentRecordings.length === 0 ? (
                <EmptyRow label="No recordings yet." />
              ) : (
                recentRecordings.map((rec) => (
                  <div
                    key={rec.id}
                    onClick={() => openRecordingDetails(rec.id)}
                    className="flex items-center justify-between border dark:border-muted/30 rounded-lg p-3 cursor-pointer hover:bg-accent-soft/50 hover:border-accent transition-colors duration-300"
                  >
                    <div className="flex gap-2 items-center">
                      <IconAvatar icon={Mic} color={"accent"} className={"rounded-full"} strokeWidth={1.2} />
                      <div className="flex flex-col">
                        <span className="font-semibold text-sm">{rec.title}</span>
                        <span className="text-xs text-muted">
                          {getRelativeDateTimeLabel(rec.meets_date, rec.start_time)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Derived from the scheduled slot — no audio-length field yet */}
                      <span className="text-sm text-muted">
                        {getReadableTimeDiff(rec.start_time, rec.end_time, "short")}
                      </span>
                      <Button
                        variant="primary"
                        isIconOnly
                        className="text-accent bg-accent-soft"
                        onPress={() => openRecordingDetails(rec.id)}
                      >
                        <Play size={16} className="fill-accent" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </Card.Content>
          </Card>
        </div>
      </div>

      <LoopDetailsDrawer state={loopDrawerState} _loop_id={selectedLoop?._id} record={selectedLoop} />

      <RecordingDetailsDrawer
        state={recordingDrawerState}
        _recording_id={selectedRecording?._id}
        record={selectedRecording}
      />

      <NetworkDetailsDrawer state={connectionDrawerState} _connection_id={selectedConnectionId} />
    </div>
  );
};

export default DashboardPage;
